import { useEffect, useRef, useState } from "react";
import { Pen, RotateCcw, Trash2, X, Check } from "lucide-react";

/**
 * Tiny per-room sketch pad. Pointer-event drawing, undo (last stroke),
 * clear (all), save (PNG data URL).
 *
 * Props:
 *   - initialDataUrl: optional starting image (data URL) to draw under new strokes
 *   - onSave(dataUrl): called when the user taps Save
 *   - onCancel(): called when the user taps Cancel / closes the pad
 *   - onClear(): called when the user taps "Delete sketch" (clears persisted)
 */
const W = 800;
const H = 500;

export const SketchPad = ({ initialDataUrl, onSave, onCancel, onClear }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  // Stack of strokes; each stroke is an array of {x, y} in canvas-logical coords.
  const strokesRef = useRef([]);
  const currentStrokeRef = useRef(null);
  const [hasContent, setHasContent] = useState(Boolean(initialDataUrl));
  const [saving, setSaving] = useState(false);

  // Prepare canvas + load any existing image as the underlay.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, W, H);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#09090b";
    ctx.lineWidth = 2.4;

    if (initialDataUrl) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, W, H);
      };
      img.src = initialDataUrl;
    }
  }, [initialDataUrl]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * W,
      y: ((e.clientY - rect.top) / rect.height) * H,
    };
  };

  const startStroke = (e) => {
    if (e.button && e.button !== 0) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture?.(e.pointerId);
    drawingRef.current = true;
    const pos = getPos(e);
    currentStrokeRef.current = [pos];
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const extendStroke = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const pos = getPos(e);
    currentStrokeRef.current.push(pos);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endStroke = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    drawingRef.current = false;
    if (currentStrokeRef.current && currentStrokeRef.current.length > 1) {
      strokesRef.current.push(currentStrokeRef.current);
      setHasContent(true);
    }
    currentStrokeRef.current = null;
  };

  const redraw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    // Reset transform to clear physical pixels, then re-apply DPR scale.
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const dpr = window.devicePixelRatio || 1;
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#09090b";
    ctx.lineWidth = 2.4;

    const drawStrokes = () => {
      for (const stroke of strokesRef.current) {
        if (!stroke.length) continue;
        ctx.beginPath();
        ctx.moveTo(stroke[0].x, stroke[0].y);
        for (let i = 1; i < stroke.length; i++) {
          ctx.lineTo(stroke[i].x, stroke[i].y);
        }
        ctx.stroke();
      }
    };

    if (initialDataUrl) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, W, H);
        drawStrokes();
      };
      img.src = initialDataUrl;
    } else {
      drawStrokes();
    }
  };

  const undo = () => {
    if (strokesRef.current.length === 0) {
      // No new strokes — but maybe the user wants to wipe the persisted image.
      // That's the Clear button's job.
      return;
    }
    strokesRef.current.pop();
    if (strokesRef.current.length === 0 && !initialDataUrl) {
      setHasContent(false);
    }
    redraw();
  };

  const clearAll = () => {
    strokesRef.current = [];
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const dpr = window.devicePixelRatio || 1;
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#09090b";
    ctx.lineWidth = 2.4;
    setHasContent(false);
  };

  const handleSave = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setSaving(true);
    try {
      // Compress to ~80% JPEG-equivalent via PNG (lossless but fine at this size)
      const dataUrl = canvas.toDataURL("image/png");
      await onSave(dataUrl);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await onClear();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="border-2 border-[#09090b] bg-white"
      data-testid="sketchpad"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#09090b] bg-[#09090b] text-white">
        <div className="flex items-center gap-2">
          <Pen size={14} />
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] font-bold">
            Sketch · pen
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={undo}
            data-testid="sketchpad-undo"
            className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-semibold bg-white text-[#09090b] hover:bg-zinc-200 inline-flex items-center gap-1"
            title="Undo last stroke"
          >
            <RotateCcw size={11} /> Undo
          </button>
          <button
            type="button"
            onClick={clearAll}
            data-testid="sketchpad-clear"
            className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-semibold bg-white text-[#09090b] hover:bg-zinc-200 inline-flex items-center gap-1"
            title="Clear all strokes"
          >
            <Trash2 size={11} /> Clear
          </button>
        </div>
      </div>
      <div className="bg-[radial-gradient(circle,#e4e4e7_1px,transparent_1px)] [background-size:16px_16px]">
        <canvas
          ref={canvasRef}
          onPointerDown={startStroke}
          onPointerMove={extendStroke}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
          onPointerLeave={endStroke}
          data-testid="sketchpad-canvas"
          style={{
            width: "100%",
            aspectRatio: `${W} / ${H}`,
            display: "block",
            touchAction: "none",
            cursor: "crosshair",
          }}
        />
      </div>
      <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-[#09090b]">
        {initialDataUrl ? (
          <button
            type="button"
            onClick={handleDelete}
            disabled={saving}
            data-testid="sketchpad-delete"
            className="px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-bold bg-white text-red-700 border border-red-300 hover:bg-red-50 inline-flex items-center gap-1 disabled:opacity-50"
          >
            <Trash2 size={12} /> Delete
          </button>
        ) : null}
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          data-testid="sketchpad-cancel"
          className="px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-100 inline-flex items-center gap-1 disabled:opacity-50"
        >
          <X size={12} /> Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || (!hasContent && strokesRef.current.length === 0)}
          data-testid="sketchpad-save"
          className="px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-bold bg-[#09090b] text-white hover:bg-[#005bea] inline-flex items-center gap-1 disabled:opacity-50"
        >
          <Check size={12} /> {saving ? "Saving…" : "Save sketch"}
        </button>
      </div>
    </div>
  );
};

export default SketchPad;
